// Settings-01 — Edit Team Member modal.
//
// First name, last name, email (read-only — safe email change is
// a later identity-pass concern), title (dropdown w/ add-new), is
// producer toggle, status select. Deactivating a member surfaces the
// warning copy the spec calls for; we don't silently reassign work
// or touch private commitments.

import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Input,
  Modal,
  Select,
  Toggle,
} from "@/design-system";
import { usePermissions } from "@/features/auth";

import { RoleCheckboxes } from "./RoleCheckboxes";
import type {
  BrokerProductRole,
  TeamMember,
  TeamMemberPatchInput,
  TeamMemberStatus,
} from "./types";


const ADD_NEW_TITLE_VALUE = "__add_new__";


// ``invited`` is a system-derived status that's set automatically
// when a Membership is created from an invitation; it's never a
// value a broker admin should pick manually. The pending-invitation
// rows in the table show Resend/Revoke instead of Edit, so the
// modal never opens with ``invited`` as the current status.
const STATUS_CHOICES: Array<{ value: TeamMemberStatus; label: string }> = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "deactivated", label: "Deactivated" },
];


export const DEACTIVATION_WARNING =
  "Deactivated users keep their historical records and cannot be " +
  "assigned new work later. Full offboarding and reassignment " +
  "workflow lands in a later pass.";


export interface TeamMemberEditModalProps {
  open: boolean;
  member: TeamMember | null;
  titleOptions: string[];
  onClose: () => void;
  onSave: (input: TeamMemberPatchInput) => Promise<void> | void;
  saving?: boolean;
  error?: string | null;
}


export function TeamMemberEditModal({
  open,
  member,
  titleOptions,
  onClose,
  onSave,
  saving = false,
  error = null,
}: TeamMemberEditModalProps) {
  const permissions = usePermissions();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [titleSelect, setTitleSelect] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [isProducer, setIsProducer] = useState(false);
  const [status, setStatus] = useState<TeamMemberStatus>("active");
  const [roles, setRoles] = useState<BrokerProductRole[]>([]);

  // Hydrate form whenever a different member opens. ``member?.member_id``
  // is the right dep — the row reference can change between fetches but
  // we only want to reset on a real identity change.
  useEffect(() => {
    if (!member) return;
    setFirstName(member.first_name ?? "");
    setLastName(member.last_name ?? "");
    setTitleSelect(member.title ?? "");
    setNewTitle("");
    setIsProducer(Boolean(member.is_producer));
    setStatus(member.status);
    setRoles([...(member.roles ?? [])]);
  }, [member?.member_id]);

  const titleSelectOptions = useMemo(() => {
    const all = new Set(titleOptions);
    if (member?.title) all.add(member.title);
    return Array.from(all).sort((a, b) => a.localeCompare(b));
  }, [titleOptions, member?.title]);

  const resolvedTitle = (
    titleSelect === ADD_NEW_TITLE_VALUE ? newTitle : titleSelect
  ).trim();

  const showDeactivationWarning =
    status === "deactivated" && member?.status !== "deactivated";

  if (!member) return null;

  const handleSave = async () => {
    const payload: TeamMemberPatchInput = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      title: resolvedTitle,
      is_producer: isProducer,
      status,
      roles,
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Edit team member"
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={onClose}
            disabled={saving}
            data-testid="team-member-edit-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            data-testid="team-member-edit-save"
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && (
          <div
            role="alert"
            data-testid="team-member-edit-error"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            value={firstName}
            data-testid="team-member-first-name"
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last name"
            value={lastName}
            data-testid="team-member-last-name"
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <Input
          label="Email"
          value={member.email}
          readOnly
          disabled
          data-testid="team-member-email"
          helper="Email changes ship with the invite/identity pass."
        />

        <div className="flex flex-col gap-1">
          <label
            htmlFor="team-member-title-select"
            className="text-sm font-medium text-text-primary"
          >
            Title
          </label>
          <Select
            id="team-member-title-select"
            data-testid="team-member-title-select"
            value={
              titleSelect === ADD_NEW_TITLE_VALUE
                ? ADD_NEW_TITLE_VALUE
                : titleSelectOptions.includes(titleSelect)
                  ? titleSelect
                  : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === ADD_NEW_TITLE_VALUE) {
                setTitleSelect(ADD_NEW_TITLE_VALUE);
              } else {
                setTitleSelect(v);
                setNewTitle("");
              }
            }}
          >
            <option value="">— None —</option>
            {titleSelectOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value={ADD_NEW_TITLE_VALUE}>+ Add new title…</option>
          </Select>
          {titleSelect === ADD_NEW_TITLE_VALUE && (
            <Input
              data-testid="team-member-title-new"
              placeholder="e.g. Director of Client Services"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          )}
        </div>

        <RoleCheckboxes
          value={roles}
          onChange={setRoles}
          disableOwnerToggle={!permissions.is_owner}
          testIdPrefix="team-member-role"
        />

        <div className="flex items-center gap-3">
          <Toggle
            checked={isProducer}
            onChange={setIsProducer}
            data-testid="team-member-is-producer"
            label="Producer"
          />
        </div>

        <Select
          label="Status"
          data-testid="team-member-status-select"
          value={status}
          onChange={(e) => setStatus(e.target.value as TeamMemberStatus)}
        >
          {STATUS_CHOICES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        {showDeactivationWarning && (
          <div
            role="alert"
            data-testid="team-member-deactivate-warning"
            className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember"
          >
            {DEACTIVATION_WARNING}
          </div>
        )}
      </div>
    </Modal>
  );
}
